import { useEffect, useRef, useState } from "react";
import { FaEye } from "react-icons/fa";
import { FiArrowLeft, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  getCategoriesList,
  getListOfTickets,
  getListOfTicketsApi,
  postRaiseTicket,
} from "../../ApiHelper/url_Helpers";
import dayjs from "dayjs";
import AttachmentPreview from "../../Components/AttachmentPreview";
import CommonTable from "../../Components/CommonTable";
import Loader from "../../Components/Loader";
import SortDropdown from "../../Components/SortDropDown";
import FilterDropdown from "../Approvals/ApprovalFilter";
import { Search } from "lucide-react";
import {
  FilterHelpDesk,
  priorityHelpDesk,
  sortHelpDesk,
} from "../Dashboard/DashboardData";

const useDebounce = (value, delay = 1000) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debouncedValue;
};

const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const FILE_SIZE = 5 * 1024 * 1024;

const validationSchema = Yup.object({
  employeeRole: Yup.object().nullable().required("Category is required"),
  description: Yup.string()
    .min(10, "Minimum 10 characters required")
    .required("Description is required"),
  priority: Yup.object().nullable().required("Priority is required"),
  attachment: Yup.array()
    // .min(1, "At least one attachment is required")
    .test("fileSize", "Each file must be ≤ 5MB", (files) =>
      files?.every((file) => file.size <= FILE_SIZE),
    )
    .test("fileFormat", "Unsupported file format", (files) =>
      files?.every((file) => SUPPORTED_FORMATS.includes(file.type)),
    ),
});

const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const HelpDesk = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fetchedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(location?.state || false);

  const [ticketsList, setTicketsList] = useState({});
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 800);

  const [page, setPage] = useState(1);
  const Categoryoptions = employeeOptions.map((item) => ({
    label: item.label,
    key: item.label,
  }));

  const [ticketFilters, setTicketFilters] = useState({
    category: "",
    status: "",
    priority: "",
    ordering: "",
  });
  const handleClose = () => {
    setShowModal(false);
    navigate(location.pathname, { replace: true, state: null });
  };

  const buildTicketParams = (filters, page, search) => {
    const params = new URLSearchParams();
    if (search) params.set("ticketId", search);
    if (filters.category) params.set("category", filters.category);
    if (filters.status) params.set("status", filters.status);
    if (filters.priority) params.set("priority", filters.priority);
    if (filters.ordering) params.set("ordering", filters.ordering);
    if (page) params.set("page", page);
    return params.toString() ? `?${params.toString()}` : "";
  };

  const fetchTickets = async (qs = "") => {
    try {
      setLoading(true);
      const listResponse = await getListOfTickets(qs);
      setTicketsList(listResponse);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const catList = await getCategoriesList();
      setEmployeeOptions(catList.map((c) => ({ value: c.id, label: c.name })));
    } catch (err) {
      console.error(err);
    }
  };

  // Pagination — fetch the next/prev page preserving current filters
  const getTicketsPaginationApi = async (pageUrl) => {
    if (!pageUrl) return;
    const pageFromApi = new URL(
      pageUrl,
      window.location.origin,
    ).searchParams.get("page");
    setPage(Number(pageFromApi));

    const qs = buildTicketParams(ticketFilters, pageFromApi, debouncedSearch);
    fetchTickets(qs);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,

      border: state.isFocused ? "2px solid #ccc" : "2px solid #e5e7eb",
      borderRadius: "0.5rem",
      minHeight: "40px",
      boxShadow: "none",
      backgroundColor: "#f9fafd",
      fontSize: "0.875rem",
      fontWeight: 300,
      "&:hover": {
        borderColor: state.isFocused ? "#ccc" : "#e5e7eb",
      },
      cursor: "pointer",
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "0.875rem",
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
          ? "#eff6ff"
          : "white",
      color: state.isSelected ? "white" : "#6a6a6a",
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 1,
      marginBottom: 1,
      zIndex: 60,
    }),
  };


  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const params = new URLSearchParams(location.search);
    const initialFilters = {
      category: params.get("category") || "",
      status: params.get("status") || "",
      priority: params.get("priority") || "",
      ordering: params.get("ordering") || "",
    };
    const initialPage = Number(params.get("page")) || 1;
    const initialSearch = params.get("ticketId") || "";

    setTicketFilters(initialFilters);
    setPage(initialPage);
    setSearchQuery(initialSearch);

    fetchTickets(buildTicketParams(initialFilters, initialPage, initialSearch));
    fetchCategories(); 
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) return;
    setPage(1);
    const qs = buildTicketParams(ticketFilters, 1, debouncedSearch);
    fetchTickets(qs);
    navigate({ pathname: location.pathname, search: qs }, { replace: true });
  }, [ticketFilters, debouncedSearch]);

  const uniqueFiles = (files) =>
    files.filter(
      (file, index, self) =>
        index === self.findIndex((f) => f.name === file.name),
    );

  const handleSubmit = async (values, { resetForm }) => {
    const formData = new FormData();
    formData.append("category", values.employeeRole?.value || "");
    formData.append("description", values.description || "");
    formData.append("subject", values.description || "");
    formData.append("priority", values.priority?.value || "");
    values.attachment.forEach((f) => formData.append("files", f));
    const response = await postRaiseTicket(formData);
    if (response) {
      fetchTickets();
      handleClose();
      resetForm();
    }
  };

  return (
    <div className="px-4 md:p-8">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Help Desk</h2>
          <p className="text-sm text-gray-500">
            Raise and track internal support tickets
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-cyan-500 text-white px-4 py-2 rounded-md"
        >
          Raise Ticket
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 justify-between">
        <div className="relative flex-grow sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search ticket ID...(TCK-000)"
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>
        <div className="flex gap-2 md:gap-4 w-full sm:w-auto overflow-visible">
          <FilterDropdown
            list={Categoryoptions}
            title="Category"
            keyName="category"
            name="Category By"
            value={ticketFilters}
            onChange={(n) => setTicketFilters((s) => ({ ...s, ...n }))}
            onClear={() => setTicketFilters((s) => ({ ...s, category: "" }))}
          />

          <FilterDropdown
            list={FilterHelpDesk}
            title="Status"
            keyName="status"
            name="Status By"
            value={ticketFilters}
            onChange={(n) => setTicketFilters((s) => ({ ...s, ...n }))}
            onClear={() => setTicketFilters((s) => ({ ...s, status: "" }))}
          />

          <FilterDropdown
            list={priorityHelpDesk}
            title="Priority"
            keyName="priority"
            name="Priority By"
            value={ticketFilters}
            onChange={(n) => {
              setTicketFilters((s) => ({ ...s, ...n }));
            }}
            onClear={() => setTicketFilters((s) => ({ ...s, priority: "" }))}
          />

          <SortDropdown
            options={sortHelpDesk}
            value={ticketFilters.ordering}
            onChange={(v) => setTicketFilters((s) => ({ ...s, ordering: v }))}
          />

          <button
            onClick={() => {
              setTicketFilters({
                category: "",
                status: "",
                priority: "",
                ordering: "",
              });
              setSearchQuery("");
              setPage(1);
            }}
            className="px-4 py-2 border rounded-md text-sm"
          >
            Reset All
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <CommonTable
          data={ticketsList?.results || []}
          rowKey="id"
          columns={[
            { key: "ticket_id", label: "Ticket ID" },
            { key: "category_name", label: "Category" },
            { key: "subject", label: "Subject" },
            { key: "priority", label: "Priority" },
            {
              key: "created_at",
              label: "Created",
              render: (v) => dayjs(v).format("DD-MM-YYYY"),
            },
            {
              key: "status",
              label: "Current Status",
              render: (v) => (
                <span
                  className={
                    v === "Open" ||  v === "open"? "text-orange-500" : "text-green-600"
                  }
                >
                  {v}
                </span>
              ),
            },
          ]}
          rowActions={(row) => (
            <FaEye
              onClick={() => navigate(`/ticket/${row.id}`)}
              className="cursor-pointer"
            />
          )}
          nextPageUrl={ticketsList?.next}
          prevPageUrl={ticketsList?.previous}
          onPageChange={getTicketsPaginationApi}
          totalCount={ticketsList?.count}
          page={page}
        />
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
          <div className="bg-white rounded-xl p-2 w-full max-w-lg">
            <div className="flex  border-b border-gray-200 pb-0.5 justify-between items-center">
              <div className="flex items-center gap-2 md:gap-3 p-4 md:p-4">
                <button
                  onClick={() => handleClose()}
                  className="text-xl md:text-2xl text-gray-700 hover:text-black transition cursor-pointer"
                >
                  <FiArrowLeft />
                </button>
                <h2 className="text-base md:text-lg font-semibold">
                  Raise a New Ticket
                </h2>
              </div>

              <div
                onClick={() => handleClose()}
                className="w-4.5 h-4.5 px-0.5 flex items-center justify-center
                          border-2 border-[#717171] rounded-md mr-6
                          cursor-pointer transition-all duration-200
                          hover:border-gray-600 hover:shadow-md"
              >
                <FiX className="w-4 h-4 font-semibold text-[#717171]" />
              </div>
            </div>
            <Formik
              initialValues={{
                employeeRole: null,
                description: "",
                attachment: [],
                priority: null,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ setFieldValue, values, touched, errors }) => (
                <Form className="space-y-3 md:space-y-4 p-4 md:p-5">
                  {/* Employee Role */}
                  <div>
                    <label className="text-sm md:text-base font-medium">
                      Select Category
                    </label>
                    <Select
                      value={values.employeeRole}
                      onChange={(val) => setFieldValue("employeeRole", val)}
                      options={employeeOptions}
                      styles={customStyles}
                    />
                    <ErrorMessage
                      name="employeeRole"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-sm md:text-base font-medium">
                      Issue Description
                    </label>
                    <Field
                      as="textarea"
                      name="description"
                      placeholder="Type here"
                      rows="2"
                      className={`w-full border-2 p-2 md:p-3 rounded-lg mt-1 h-20 md:h-28 bg-[#f9fafd] text-sm
                                  ${
                                    touched.description && errors.description
                                      ? "border-red-400"
                                      : "border-gray-200"
                                  }`}
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Attachment */}
                  {values.attachment && values.attachment.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2">
                        Attached Files ({values.attachment.length})
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {values.attachment?.map((file, index) => (
                          <AttachmentPreview
                            key={`${file.name}-${index}`}
                            file={file}
                            onRemove={() => {
                              setFieldValue(
                                "attachment",
                                values.attachment.filter((_, i) => i !== index),
                              );
                            }}
                            isRemove={true}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm md:text-base font-medium">
                      Attachment
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.currentTarget.files || []);
                        setFieldValue(
                          "attachment",
                          uniqueFiles([...values.attachment, ...files]),
                        );
                      }}
                      className={`w-full border-2 p-2 rounded-lg mt-1 bg-[#f9fafd] text-sm
                                  ${touched.attachment && errors.attachment ? "border-red-400" : "border-gray-200"}
                                `}
                    />
                    <ErrorMessage
                      name="attachment"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="text-sm md:text-base font-medium block">
                      Priority
                    </label>
                    <Select
                      value={values.priority}
                      onChange={(val) => setFieldValue("priority", val)}
                      options={priorityOptions}
                      styles={{
                        ...customStyles,
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                      menuPlacement="top"
                      menuPortalTarget={document.body}
                    />
                    <ErrorMessage
                      name="priority"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col md:flex-row justify-end mt-3 gap-5 md:gap-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-3 md:px-4 py-1.5 md:py-2 text-black rounded-md hover:bg-gray-100 cursor-pointer font-semibold"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="px-4 md:px-5 py-1.5 md:py-3 bg-[#05c1e2] text-white rounded-md hover:bg-[#237595] cursor-pointer font-semibold"
                    >
                      Submit Ticket
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpDesk;
